// Directory Console — interactive geo coverage with a D3 world choropleth,
// per-country inspector drawer, data quality, types, thin coverage, CSV export.
import React, { useState, useEffect, useRef } from 'react';
import { ConsoleShell, KPI, Panel, SectionLabel, Bar, StatLine, Empty, Drawer, ExportButton, Donut, Gauge, PALETTE, useConsoleTheme, useIsMobile } from '../components/Console';
import { useCommandGeo } from '../hooks/useAnalytics';
import { supabase } from '../supabaseClient';
import { setFocus, takeFocus } from '../utils/consoleBus';

const fmt = n => (n == null ? '—' : n >= 1e6 ? (n / 1e6).toFixed(2).replace(/\.?0+$/, '') + 'M' : Number(n).toLocaleString());

// DB country name -> Natural Earth (world-atlas) feature name, for the few that differ.
const ALIAS = {
  'United States': 'United States of America',
  'Tanzania': 'United Republic of Tanzania',
  'Serbia': 'Republic of Serbia',
  'Czech Republic': 'Czechia',
  'Bosnia and Herzegovina': 'Bosnia and Herz.',
  'North Macedonia': 'Macedonia',
  'Dominican Republic': 'Dominican Rep.',
  'Ivory Coast': "Côte d'Ivoire",
  'Democratic Republic of the Congo': 'Dem. Rep. Congo',
  'Republic of the Congo': 'Congo',
  'South Sudan': 'S. Sudan',
};
function loadScript(src) {
  return new Promise((res, rej) => {
    if (Array.from(document.scripts).some(s => s.src === src)) return res();
    const el = document.createElement('script');
    el.src = src; el.onload = res; el.onerror = rej;
    document.head.appendChild(el);
  });
}

function WorldMap({ countries, onSelect }) {
  const { t } = useConsoleTheme();
  const ref = useRef(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadScript('https://cdnjs.cloudflare.com/ajax/libs/d3/7.8.5/d3.min.js');
        await loadScript('https://cdnjs.cloudflare.com/ajax/libs/topojson/3.0.2/topojson.min.js');
        const d3 = window.d3, topojson = window.topojson;
        if (!d3 || !topojson) { if (!cancelled) setErr(true); return; }
        const topo = await d3.json('https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json');
        if (cancelled || !ref.current) return;
        const byNE = {};
        (countries || []).forEach(c => { byNE[ALIAS[c.country] || c.country] = c; });
        const fc = topojson.feature(topo, topo.objects.countries);
        const W = 900, Hh = 440;
        const proj = d3.geoNaturalEarth1().fitSize([W, Hh], fc);
        const path = d3.geoPath(proj);
        const maxN = Math.max(1, ...(countries || []).map(c => c.n));
        const color = d3.scaleSequential(d3.interpolate(t.gbar, t.accent)).domain([0, Math.log10(maxN + 1)]);
        ref.current.innerHTML = '';
        const svg = d3.select(ref.current).append('svg').attr('viewBox', `0 0 ${W} ${Hh}`).attr('width', '100%').style('display', 'block');
        const tip = d3.select(ref.current).append('div')
          .style('position', 'absolute').style('pointer-events', 'none').style('background', t.tip)
          .style('color', '#fff').style('font-size', '11px').style('padding', '3px 7px')
          .style('border-radius', '5px').style('opacity', 0).style('z-index', 30).style('white-space', 'nowrap');
        svg.selectAll('path').data(fc.features).join('path')
          .attr('d', path)
          .attr('stroke', t.bd).attr('stroke-width', 0.4)
          .attr('fill', d => { const c = byNE[d.properties.name]; return c ? color(Math.log10(c.n + 1)) : t.gbar; })
          .style('cursor', d => (byNE[d.properties.name] ? 'pointer' : 'default'))
          .on('mousemove', function (e, d) {
            const c = byNE[d.properties.name];
            tip.style('opacity', c ? 1 : 0).html(c ? `${d.properties.name}: ${c.n.toLocaleString()}` : '')
              .style('left', (e.offsetX + 12) + 'px').style('top', (e.offsetY + 12) + 'px');
            if (c) d3.select(this).attr('stroke', t.accent).attr('stroke-width', 1);
          })
          .on('mouseleave', function () { tip.style('opacity', 0); d3.select(this).attr('stroke', t.bd).attr('stroke-width', 0.4); })
          .on('click', function (e, d) { const c = byNE[d.properties.name]; if (c && onSelect) onSelect(c.country); });
      } catch (_e) { if (!cancelled) setErr(true); }
    })();
    return () => { cancelled = true; };
  }, [countries, t]);
  if (err) return <Empty>Map unavailable — use the country list below.</Empty>;
  return <div ref={ref} style={{ position: 'relative', width: '100%', minHeight: 200 }} />;
}

function CountryInspector({ country }) {
  const { t } = useConsoleTheme();
  const [d, setD] = useState(null);
  useEffect(() => {
    let c = false; setD(null);
    supabase.rpc('admin_country_detail', { p_country: country }).then(({ data }) => { if (!c) setD(data || {}); });
    return () => { c = true; };
  }, [country]);
  if (!d) return <Empty>Loading {country}…</Empty>;
  const photoPct = d.n ? Math.round(d.with_photo / d.n * 100) : 0;
  const floorPct = d.n ? Math.round(d.floor_ok / d.n * 100) : 0;
  const types = d.top_types || [];
  const maxT = Math.max(1, ...types.map(x => x.n));
  const cities = d.top_cities || [];
  const maxC = Math.max(1, ...cities.map(x => x.n));
  return (
    <div>
      <div style={{ fontSize:'1.4rem', fontWeight:500, color:t.tx, marginBottom:2 }}>{fmt(d.n)}</div>
      <div style={{ fontSize:'.66rem', color:t.mut, marginBottom:12 }}>records · {fmt(d.cities)} cities</div>
      <StatLine label="Display-floor pass" value={`${floorPct}%`} />
      <StatLine label="Has photo" value={`${photoPct}%`} color={photoPct < 5 ? t.warn : t.tx} />
      <StatLine label="Has phone" value={d.n ? `${Math.round(d.with_phone / d.n * 100)}%` : '—'} />
      <div style={{ height:1, background:t.bd, margin:'12px 0' }} />
      <div style={{ fontSize:'.66rem', color:t.mut, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:8 }}>Top types</div>
      {types.length === 0 ? <Empty>No type data.</Empty> : types.map(x => <Bar key={x.type} label={x.type} value={fmt(x.n)} pct={Math.round(x.n / maxT * 100)} labelWidth={120} />)}
      <div style={{ fontSize:'.66rem', color:t.mut, textTransform:'uppercase', letterSpacing:'.06em', margin:'12px 0 8px' }}>Top cities</div>
      {cities.length === 0 ? <Empty>No city data.</Empty> : cities.map(x => <Bar key={x.city} label={x.city} value={fmt(x.n)} pct={Math.round(x.n / maxC * 100)} color={t.accent} labelWidth={120} />)}
    </div>
  );
}

function TypeInspector({ type }) {
  const { t } = useConsoleTheme();
  const [d, setD] = useState(null);
  useEffect(() => {
    let c = false; setD(null);
    supabase.rpc('admin_type_detail', { p_type: type }).then(({ data }) => { if (!c) setD(data || {}); });
    return () => { c = true; };
  }, [type]);
  if (!d) return <Empty>Loading {type}…</Empty>;
  const photoPct = d.n ? Math.round(d.with_photo / d.n * 100) : 0;
  const floorPct = d.n ? Math.round(d.floor_ok / d.n * 100) : 0;
  const countries = d.top_countries || []; const maxC = Math.max(1, ...countries.map(x => x.n));
  const cities = d.top_cities || []; const maxCi = Math.max(1, ...cities.map(x => x.n));
  return (
    <div>
      <div style={{ fontSize:'1.4rem', fontWeight:500, color:t.tx, marginBottom:2 }}>{fmt(d.n)}</div>
      <div style={{ fontSize:'0.85rem', color:t.mut, marginBottom:12 }}>venues · {fmt(d.countries)} countries</div>
      <StatLine label="Display-floor pass" value={`${floorPct}%`} />
      <StatLine label="Has photo" value={`${photoPct}%`} color={photoPct < 5 ? t.warn : t.tx} />
      <div style={{ height:1, background:t.bd, margin:'12px 0' }} />
      <div style={{ fontSize:'0.8rem', color:t.mut, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:8 }}>Top countries</div>
      {countries.length === 0 ? <Empty>No data.</Empty> : countries.map(x => <Bar key={x.country} label={x.country} value={fmt(x.n)} pct={Math.round(x.n / maxC * 100)} labelWidth={120} />)}
      <div style={{ fontSize:'0.8rem', color:t.mut, textTransform:'uppercase', letterSpacing:'.06em', margin:'12px 0 8px' }}>Top cities</div>
      {cities.length === 0 ? <Empty>No data.</Empty> : cities.map(x => <Bar key={x.city} label={x.city} value={fmt(x.n)} pct={Math.round(x.n / maxCi * 100)} color={t.accent} labelWidth={120} />)}
    </div>
  );
}

function Body({ onNav }) {
  const { t } = useConsoleTheme();
  const mobile = useIsMobile();
  const { data: geo, loading, refresh } = useCommandGeo();
  const [sel, setSel] = useState(null);
  const [selType, setSelType] = useState(null);
  useEffect(() => { const f = takeFocus(['country', 'type']); if (f) { if (f.type === 'country') setSel(f.value); else setSelType(f.value); } }, []);
  const top = (geo && geo.top) || [];
  const maxN = top.length ? top[0].n : 0;
  const types = (geo && geo.top_types) || [];
  const maxT = types.length ? types[0].n : 0;
  const thin = (geo && geo.thin) || [];
  const all = (geo && geo.all_countries) || [];
  const floorPct = geo && geo.total ? Math.round(geo.floor_ok / geo.total * 100) : 0;
  const photoPct = geo && geo.total ? Math.round(geo.with_photo / geo.total * 100) : 0;
  const phonePct = geo && geo.total ? Math.round(geo.with_phone / geo.total * 100) : 0;
  const col = n => (mobile ? '1fr 1fr' : `repeat(${n},1fr)`);
  const typeDonut = (() => {
    const tt = (geo && geo.top_types) || [];
    const tot = (geo && geo.total) || 0;
    const top = tt.slice(0, 6);
    const sum = top.reduce((a, x) => a + x.n, 0);
    const arr = top.map((x, i) => ({ label: x.type, value: x.n, color: PALETTE[i % PALETTE.length] }));
    if (tot - sum > 0) arr.push({ label: 'Other types', value: tot - sum, color: t.mut });
    return arr;
  })();
  const exportCols = [{ label:'Country', key:'country' }, { label:'Records', key:'n' }, { label:'With photo', key:'with_photo' }];
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns: col(5), gap:10 }}>
        <KPI label="Records" value={geo ? fmt(geo.total) : '…'} sub="directory" accent={t.accent} />
        <KPI label="Countries" value={geo ? fmt(geo.countries) : '…'} accent="#314F5C" />
        <KPI label="Cities" value={geo ? fmt(geo.cities) : '…'} accent="#5a8fa6" />
        <KPI label="Types" value={geo ? fmt(geo.types) : '…'} accent="#819D84" />
        <KPI label="Floor pass" value={`${floorPct}%`} sub="display-ready" subColor={t.up} accent="#17a86a" />
      </div>

      <SectionLabel right="shaded by record count · click a country">Coverage map</SectionLabel>
      <Panel>
        {all.length === 0 ? <Empty>Loading map…</Empty> : <WorldMap countries={all} onSelect={setSel} />}
      </Panel>

      <SectionLabel right="records · photo coverage">Geographic distribution</SectionLabel>
      <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr' : '1.35fr 1fr', gap:12 }}>
        <Panel title="◷ Top countries" right={<ExportButton filename="sanoire-countries" cols={exportCols} rows={all.length ? all : top} />}>
          {loading && !top.length ? <Empty>Loading…</Empty>
            : top.map(g => {
                const pc = g.n ? Math.round(g.with_photo / g.n * 100) : 0;
                return (
                  <div key={g.country} onClick={() => setSel(g.country)} style={{ display:'flex', alignItems:'center', gap:9, marginBottom:8, fontSize:'.72rem', cursor:'pointer' }}>
                    <span style={{ width:104, flexShrink:0, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', color:t.tx }}>{g.country}</span>
                    <div style={{ flex:1, height:14, borderRadius:4, position:'relative', overflow:'hidden', background:t.gbar }}>
                      <div style={{ position:'absolute', left:0, top:0, bottom:0, borderRadius:4, width:`${Math.round(g.n / maxN * 100)}%`, background:t.accent, opacity:.32 }} />
                      <div style={{ position:'absolute', left:0, top:0, bottom:0, borderRadius:4, width:`${Math.round(g.with_photo / maxN * 100)}%`, background:t.up }} />
                    </div>
                    <span style={{ width:52, textAlign:'right', color:t.mut, fontVariantNumeric:'tabular-nums' }}>{fmt(g.n)}</span>
                    <span style={{ width:34, textAlign:'right', color: pc < 5 ? t.warn : t.mut, fontVariantNumeric:'tabular-nums' }}>{pc}%</span>
                  </div>
                );
              })}
        </Panel>
        <Panel title="◈ Data quality" right={<span onClick={refresh} style={{ cursor:'pointer' }}>refresh</span>}>
          <StatLine label="Display-floor pass" value={`${floorPct}%`} />
          <StatLine label="Has phone" value={`${phonePct}%`} />
          <StatLine label="Has photo" value={`${photoPct}%`} color={t.warn} />
          <StatLine label="Countries · cities" value={geo ? `${fmt(geo.countries)} · ${fmt(geo.cities)}` : '…'} />
          <div style={{ height:1, background:t.bd, margin:'10px 0' }} />
          <div style={{ fontSize:'.7rem', borderRadius:7, padding:'8px 10px', lineHeight:1.5, background:t.alert, color:t.alertc, border:`1px solid ${t.alertbd}` }}>
            ⚠ {geo ? fmt(geo.total - geo.with_photo) : '…'} below photo tier{geo && geo.zero_photo ? ` — ${geo.zero_photo} countries at 0%` : ''}
          </div>
        </Panel>
      </div>

      <SectionLabel right="live">Quality gauges</SectionLabel>
      <Panel>
        <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr 1fr' : 'repeat(4,1fr)', gap:12 }}>
          <Gauge value={floorPct} label="Display-floor pass" />
          <Gauge value={photoPct} label="Has photo" />
          <Gauge value={phonePct} label="Has phone" />
          <Gauge value={geo && geo.countries ? Math.round((geo.countries - (geo.thin_count || 0)) / geo.countries * 100) : 0} label="Country depth" />
        </div>
      </Panel>

      <SectionLabel>Composition</SectionLabel>
      <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap:12 }}>
        <Panel title="◔ Location types" right={geo ? `${geo.types} types` : ''}>
          {types.length === 0 ? <Empty>Loading…</Empty>
            : types.map(ty => <Bar key={ty.type} label={ty.type} value={fmt(ty.n)} pct={Math.round(ty.n / maxT * 100)} labelWidth={130} hint={`${ty.type} — ${fmt(ty.n)} venues — click to inspect`} onClick={() => setSelType(ty.type)} />)}
        </Panel>
        <Panel title="◌ Thin coverage" right={geo ? `${geo.thin_count} countries < 25` : ''}>
          {thin.length === 0 ? <Empty>No thin countries — every country has depth.</Empty>
            : thin.map(c => (
                <div key={c.country} onClick={() => setSel(c.country)} style={{ display:'flex', justifyContent:'space-between', fontSize:'.72rem', padding:'4px 0', borderBottom:`.5px solid ${t.bd}`, cursor:'pointer' }}>
                  <span style={{ color:t.tx }}>{c.country}</span><b style={{ color:t.warn, fontWeight:500 }}>{c.n}</b>
                </div>
              ))}
        </Panel>
      </div>

      <SectionLabel right={geo ? `${geo.types} types` : ''}>Type distribution</SectionLabel>
      <Panel>
        {!geo ? <Empty>Loading…</Empty> : <Donut data={typeDonut} center={{ value: fmt(geo.total), label: 'records' }} />}
      </Panel>

      <Drawer open={!!sel} onClose={() => setSel(null)} title={sel || 'Country'}>
        {sel && <CountryInspector country={sel} />}
        {sel && <div style={{ marginTop:16 }}><button onClick={() => { setFocus({ type:'sana-country', value:sel }); onNav && onNav('sana'); }} style={{ fontSize:'0.82rem', padding:'7px 12px', borderRadius:7, cursor:'pointer', border:`1px solid ${t.bd}`, background:t.period, color:t.tx, fontWeight:500 }}>Sana activity →</button></div>}
      </Drawer>

      <Drawer open={!!selType} onClose={() => setSelType(null)} title={selType || 'Type'}>
        {selType && <TypeInspector type={selType} />}
      </Drawer>
    </div>
  );
}

export default function DirectoryConsole(props) {
  return <ConsoleShell title="Directory Console"><Body onNav={props.onNav} /></ConsoleShell>;
}
